/**
 * useDatasetRecords — Inteligência de Dados (Fase 3)
 * -----------------------------------------------------------------------------
 * Hooks de LEITURA dos registros importados de sistemas externos (Explorador).
 *
 * Regras de arquitetura:
 *   - 100% via RPC SECURITY DEFINER (nada de SELECT direto em `external_data`).
 *   - Filtros/ordenação viajam por `json_path`; o backend valida cada path
 *     contra o dicionário de campos do dataset (whitelist anti-injeção).
 *   - Paginação e contagem são feitas no servidor (nunca carrega tudo).
 *
 * @version 1.0.0
 * RELEASE: 2026-08-23 v1.0.0 - Criação inicial (Fase 3 - Explorador de Dados).
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('DatasetRecords');

// =============================================================================
// TIPOS
// =============================================================================

/** Operadores aceitos pela RPC `query_external_records` */
export type DatasetFilterOp =
  | 'eq'
  | 'neq'
  | 'contains'
  | 'starts_with'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'empty'
  | 'not_empty';

/** Filtro aplicado a um campo do payload */
export interface DatasetFilter {
  path: string;
  op: DatasetFilterOp;
  value?: string;
}

/** Registro externo retornado pela RPC */
export interface DatasetRecord {
  id: string;
  external_id: string;
  payload: Record<string, unknown>;
  source_created_at: string | null;
  source_updated_at: string | null;
  imported_at: string;
  total_count: number;
}

/** Cabeçalho do dataset no Explorador */
export interface DatasetOverview {
  id: string;
  name: string;
  description: string | null;
  provider_name: string;
  connection_name: string;
  operation_code: string;
  status: string;
  record_count: number;
  fields_count: number;
  last_import_at: string | null;
  has_data: boolean;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Extrai o valor de um `json_path` (ex.: "cliente.nome") de dentro do payload.
 * Mantém a mesma semântica do backend (`payload #>> '{cliente,nome}'`).
 */
export function getPayloadValue(
  payload: Record<string, unknown> | null | undefined,
  jsonPath: string,
): string {
  if (!payload) return '';
  const parts = jsonPath.split('.');
  let current: unknown = payload;

  for (const part of parts) {
    if (current === null || current === undefined || typeof current !== 'object') return '';
    current = (current as Record<string, unknown>)[part];
  }

  if (current === null || current === undefined) return '';
  if (typeof current === 'object') return JSON.stringify(current);
  return String(current);
}

// =============================================================================
// HOOK — REGISTROS PAGINADOS
// =============================================================================

/** Consulta paginada dos registros do dataset ativo */
export function useDatasetRecords(options: {
  datasetId: string | null;
  search?: string;
  filters?: DatasetFilter[];
  sortPath?: string | null;
  sortDir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}) {
  const {
    datasetId,
    search = '',
    filters = [],
    sortPath = null,
    sortDir = 'desc',
    page = 0,
    pageSize = 50,
  } = options;

  // Apenas filtros utilizáveis são enviados (evita round-trip inútil)
  const activeFilters = filters.filter(
    (f) => f.path && (f.op === 'empty' || f.op === 'not_empty' || (f.value ?? '') !== ''),
  );

  const recordsQuery = useQuery({
    queryKey: [
      'external-records',
      datasetId,
      search,
      activeFilters,
      sortPath,
      sortDir,
      page,
      pageSize,
    ],
    enabled: Boolean(datasetId),
    staleTime: 30 * 1000,
    queryFn: async (): Promise<DatasetRecord[]> => {
      const { data, error } = await supabase.rpc('query_external_records', {
        p_dataset_id: datasetId as string,
        p_search: search.trim() || null,
        p_filters: activeFilters as never,
        p_sort_path: sortPath,
        p_sort_dir: sortDir,
        p_limit: pageSize,
        p_offset: page * pageSize,
      });
      if (error) {
        logger.error('Falha ao consultar registros', { message: error.message });
        throw error;
      }
      return (data ?? []) as unknown as DatasetRecord[];
    },
  });

  const records = recordsQuery.data ?? [];

  return {
    records,
    totalCount: Number(records[0]?.total_count ?? 0),
    isLoading: recordsQuery.isLoading,
    isFetching: recordsQuery.isFetching,
    error: recordsQuery.error as Error | null,
    refetch: recordsQuery.refetch,
  };
}

// =============================================================================
// HOOK — RESUMO DO DATASET
// =============================================================================

/** Metadados do dataset (cabeçalho do Explorador) */
export function useDatasetOverview(datasetId: string | null) {
  const overviewQuery = useQuery({
    queryKey: ['external-dataset-overview', datasetId],
    enabled: Boolean(datasetId),
    staleTime: 60 * 1000,
    queryFn: async (): Promise<DatasetOverview | null> => {
      const { data, error } = await supabase.rpc('get_external_dataset_overview', {
        p_dataset_id: datasetId as string,
      });
      if (error) throw error;
      const rows = (data ?? []) as unknown as DatasetOverview[];
      return rows[0] ?? null;
    },
  });

  return {
    overview: overviewQuery.data ?? null,
    isLoading: overviewQuery.isLoading,
  };
}

// =============================================================================
// HOOK — VALORES DISTINTOS DE UM CAMPO (filtros de seleção)
// =============================================================================

/** Valores mais frequentes de um campo — usado nos filtros rápidos */
export function useDatasetFieldValues(datasetId: string | null, jsonPath: string | null) {
  const valuesQuery = useQuery({
    queryKey: ['external-dataset-field-values', datasetId, jsonPath],
    enabled: Boolean(datasetId && jsonPath),
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<{ value: string; total: number }[]> => {
      const { data, error } = await supabase.rpc('get_external_dataset_field_values', {
        p_dataset_id: datasetId as string,
        p_json_path: jsonPath as string,
        p_limit: 50,
      });
      if (error) throw error;
      return (data ?? []) as unknown as { value: string; total: number }[];
    },
  });

  return {
    values: valuesQuery.data ?? [],
    isLoading: valuesQuery.isLoading,
  };
}

// =============================================================================
// HOOK — COMPARAÇÃO COM ENTIDADES INTERNAS (Fase 3 — Comparar)
// =============================================================================
// RELEASE: 2026-08-23 v1.1.0 - Adicionado useDatasetComparison (MATCH/NOT_MATCH).

/** Entidades internas comparáveis */
export type CompareTarget = 'customer' | 'contact' | 'vehicle';

/** Chave de comparação aceita por entidade (validada também no backend) */
export type CompareKey = 'document' | 'name' | 'phone' | 'email' | 'plate' | 'chassis' | 'renavam';

/** Chaves permitidas por entidade — espelha a whitelist da RPC */
export const COMPARE_KEYS_BY_TARGET: Record<CompareTarget, { value: CompareKey; label: string }[]> = {
  customer: [
    { value: 'document', label: 'CPF / CNPJ' },
    { value: 'name', label: 'Nome / Razão social' },
  ],
  contact: [
    { value: 'phone', label: 'Telefone' },
    { value: 'email', label: 'E-mail' },
    { value: 'name', label: 'Nome' },
  ],
  vehicle: [
    { value: 'plate', label: 'Placa' },
    { value: 'chassis', label: 'Chassi' },
    { value: 'renavam', label: 'Renavam' },
  ],
};

/** Linha retornada pela comparação */
export interface DatasetComparisonRow {
  record_id: string;
  external_id: string;
  payload: Record<string, unknown>;
  match_value: string | null;
  matched_entity_id: string | null;
  matched_entity_label: string | null;
  total_count: number;
  total_match: number;
  total_not_match: number;
}

/**
 * Compara os registros do dataset com clientes, contatos ou veículos do tenant.
 * A normalização (documento, placa, telefone) é feita no banco para garantir
 * o mesmo critério dos índices e evitar tráfego desnecessário.
 */
export function useDatasetComparison(options: {
  datasetId: string | null;
  target: CompareTarget;
  matchPath: string | null;
  matchKey: CompareKey;
  mode: 'MATCH' | 'NOT_MATCH';
  search?: string;
  page?: number;
  pageSize?: number;
  enabled?: boolean;
}) {
  const {
    datasetId,
    target,
    matchPath,
    matchKey,
    mode,
    search = '',
    page = 0,
    pageSize = 50,
    enabled = true,
  } = options;

  const comparisonQuery = useQuery({
    queryKey: [
      'external-comparison',
      datasetId,
      target,
      matchPath,
      matchKey,
      mode,
      search,
      page,
      pageSize,
    ],
    enabled: Boolean(enabled && datasetId && matchPath),
    staleTime: 30 * 1000,
    queryFn: async (): Promise<DatasetComparisonRow[]> => {
      const { data, error } = await supabase.rpc('compare_external_dataset', {
        p_dataset_id: datasetId as string,
        p_target: target,
        p_match_path: matchPath as string,
        p_match_key: matchKey,
        p_mode: mode,
        p_search: search.trim() || null,
        p_limit: pageSize,
        p_offset: page * pageSize,
      });
      if (error) {
        logger.error('Falha ao comparar dataset', { message: error.message });
        throw error;
      }
      return (data ?? []) as unknown as DatasetComparisonRow[];
    },
  });

  const rows = comparisonQuery.data ?? [];

  return {
    rows,
    totalCount: Number(rows[0]?.total_count ?? 0),
    totalMatch: Number(rows[0]?.total_match ?? 0),
    totalNotMatch: Number(rows[0]?.total_not_match ?? 0),
    isLoading: comparisonQuery.isLoading,
    isFetching: comparisonQuery.isFetching,
    error: comparisonQuery.error as Error | null,
    refetch: comparisonQuery.refetch,
  };
}
